import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { lazy, Suspense, useEffect, useState } from "react";

// Lazy-load the modal (and its heavy deps: Supabase client, Dialog, Select,
// form validation). The button itself is rendered everywhere — Hero, Header,
// Footer, CTASection — so eager-importing the modal pulled ~150KB of JS into
// the main bundle on every page, even when nobody clicks the CTA.
const importDemoModal = () => import("@/components/DemoRequestModal");
const DemoRequestModal = lazy(importDemoModal);

// Warm the modal chunk (and its Supabase/Dialog/validation deps) before the
// click so the dialog opens instantly instead of waiting on a network fetch.
// Vite dedupes the dynamic import, so calling this repeatedly is cheap.
const prefetchDemoModal = () => {
  importDemoModal().catch(() => {});
};

// Once after first paint, warm the (~10KB) modal chunk during browser idle
// time. This makes click->open instant even on mobile (no hover) and for
// immediate clicks, without competing with the page's critical resources.
let idlePrefetchScheduled = false;
const scheduleIdlePrefetch = () => {
  if (idlePrefetchScheduled || typeof window === "undefined") return;
  idlePrefetchScheduled = true;
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  }).requestIdleCallback;
  if (ric) ric(prefetchDemoModal, { timeout: 3000 });
  else window.setTimeout(prefetchDemoModal, 2000);
};

export interface PricingMeta {
  planName: string;
  teamSize: number;
  billingCycle: "monthly" | "quarterly" | "yearly";
  totalPrice: number | null;
  pricePerUser: number | null;
  currency: string;
}

interface BookDemoButtonProps {
  variant?: "default" | "withArrow" | "hero";
  fullWidth?: boolean;
  className?: string;
  label?: string;
  buttonClassName?: string;
  pricingMeta?: PricingMeta;
  /** Lead source tag forwarded to the demo modal (DB + CRM). */
  source?: string;
}

const BookDemoButton = ({
  variant = "default",
  fullWidth = false,
  className,
  label,
  buttonClassName,
  pricingMeta,
  source,
}: BookDemoButtonProps) => {
  const { t } = useTranslation();
  const text = label || t("nav.bookDemo");
  // Only mount the modal after the user clicks once — keeps Supabase + dialog
  // chunks out of the critical path entirely.
  const [mounted, setMounted] = useState(false);

  // Warm the modal chunk during idle time so the first click opens instantly.
  useEffect(() => {
    scheduleIdlePrefetch();
  }, []);

  const handleClick = () => {
    setMounted(true);
    if (pricingMeta) {
      import("@/lib/analytics")
        .then(({ track }) => track("pricing_plan_clicked", {
          plan_name: pricingMeta.planName,
          team_size: pricingMeta.teamSize,
          billing_cycle: pricingMeta.billingCycle,
          total_price: pricingMeta.totalPrice,
          price_per_user: pricingMeta.pricePerUser,
          currency: pricingMeta.currency,
        }))
        .catch(() => {});
    }
  };

  const button = (
    <Button
        onClick={handleClick}
        onMouseEnter={prefetchDemoModal}
        onFocus={prefetchDemoModal}
        onTouchStart={prefetchDemoModal}
        className={cn(
          "bg-[#4A7BF7] hover:bg-[#3A6BE7] text-white font-semibold rounded-full",
          variant === "hero" && "group px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg",
          variant === "withArrow" && "group px-6 py-5",
          variant === "default" && "px-8 py-6 text-base",
          fullWidth && "w-full",
          buttonClassName,
          className,
        )}
    >
        {text}
        {(variant === "withArrow" || variant === "hero") && (
          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        )}
    </Button>
  );

  if (!mounted) return button;

  return (
    <Suspense fallback={button}>
      <DemoRequestModal pricingMeta={pricingMeta} source={source} defaultOpen>
        {button}
      </DemoRequestModal>
    </Suspense>
  );
};

export default BookDemoButton;
